import { z } from 'zod';
import { USER_NAME_EXTENSIONS } from './voice-app-access';
import { ROLES } from './rbac';
import { autoAttendantWizardAnswersSchema, callQueueWizardAnswersSchema } from './wizard';
import {
  DEPLOYMENT_SHEETS,
  AA_CALLABLE_ENTITY_KINDS,
  AA_DIRECTORY_SEARCH_METHODS,
  AA_DTMF_RESPONSES,
  AA_MENU_OPTION_ACTIONS,
  AA_SCHEDULE_TYPES,
  BUG_SEVERITIES,
  BUG_STATUSES,
  BUSY_ON_BUSY_OPTIONS,
  CALLER_ID_OPTIONS,
  CALL_FORWARDING_TYPES,
  CALL_GROUP_ORDERS,
  CALL_QUEUE_NO_AGENT_ACTIONS,
  CALL_QUEUE_NO_AGENT_APPLY_TO,
  CALL_QUEUE_OVERFLOW_ACTIONS,
  CALL_QUEUE_ROUTING_METHODS,
  CALL_QUEUE_TIMEOUT_ACTIONS,
  CALL_TARGET_TYPES,
  FEATURE_AREAS,
  FEATURE_PRIORITIES,
  FEATURE_STATUSES,
  FLOW_KINDS,
  GREETING_TYPES,
  LICENSING_MODELS,
  NETWORK_SCOPES,
  HANDOVER_NOTE_SECTIONS,
  NETWORK_TYPES,
  NUMBER_RANGE_KINDS,
  SITE_REGIONS,
  NUMBER_STATUSES,
  NUMBER_TYPES,
  RESOURCE_ACCOUNT_KINDS,
  TENANT_OBJECT_TYPES,
  TENANT_POLICY_TYPES,
  VOICEMAIL_PROMPT_LANGUAGE_CODES,
  normalizeVoicemailLanguage,
} from './domain';
import { HEX_COLOR_RE } from './color';
import { MAX_CONTACTS_PER_ROLE } from './site-contacts';
import { isHandoverSectionKey } from './handover';
import {
  COUNTRY_CODES,
  SITE_MODES,
  SR_MENU_KEYS,
  SR_PRIORITIES,
  SR_STATUSES,
  SR_TARGET_KINDS,
  SR_TYPES,
  SR_TYPE_DEFS,
  srFieldVisible,
  type SrFieldSpec,
  type SrType,
} from './service-requests';

export const emailSchema = z.string().email().max(320).transform((s) => s.toLowerCase().trim());

/**
 * A Teams UPN. Zod's built-in .email() rejects non-ASCII local parts (e.g.
 * "Taïs.DeWinter@..."), which real-world M365 UPNs derived from a person's
 * name legitimately contain. Deliberately as tolerant as the browser-side
 * import preview's own check, so the server never disagrees with what the
 * user was shown as "Ready".
 */
export const upnSchema = z
  .string()
  .trim()
  .max(320)
  .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Invalid UPN')
  .transform((s) => s.toLowerCase());

/** NIST-ish: length over complexity. */
export const passwordSchema = z
  .string()
  .min(12, 'Use at least 12 characters')
  .max(200);

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(200),
  totp: z.string().regex(/^\d{6}$/).optional(),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const totpEnrolConfirmSchema = z.object({
  totp: z.string().regex(/^\d{6}$/),
});

/** Start (re-)enrolment. `currentTotp` is required once the account already has confirmed MFA - re-enrolling must prove possession of the current authenticator before its secret can be replaced. */
// Body is optional (a bare Generate click sends none) - preprocess it to {}.
// siteIds: limit the pack to these sites (empty/absent = every site).
// sectionKeys: limit it to these template sections (empty/absent = all of them).
export const handoverGenerateSchema = z.preprocess(
  (v) => v ?? {},
  z.object({
    notes: z.string().trim().max(2000).optional(),
    siteIds: z.array(z.guid()).max(200).optional(),
    sectionKeys: z.array(z.string().refine(isHandoverSectionKey, 'Unknown handover section')).max(20).optional(),
  }),
);

/** Writes (or, with an empty body, clears) the note for one section - for one site, or for every site (siteId null). */
export const handoverNoteSchema = z.object({
  sectionKey: z.enum(HANDOVER_NOTE_SECTIONS),
  siteId: z.guid().nullable().optional(),
  body: z.string().max(20000),
});

export const totpEnrolStartSchema = z.preprocess(
  (v) => v ?? {},
  z.object({ currentTotp: z.string().regex(/^\d{6}$/).optional() }),
);

/** Set a new password on the forced first-sign-in reset (before MFA enrolment). */
export const passwordChangeSchema = z.object({
  newPassword: passwordSchema,
});
export type PasswordChangeInput = z.infer<typeof passwordChangeSchema>;

export const createUserSchema = z.object({
  email: emailSchema,
  displayName: z.string().min(1).max(120),
  role: z.enum(ROLES),
  // No password: the system generates a temporary one, emails it, and forces a
  // reset on first sign-in.
  tenantIds: z.array(z.guid()).optional(),
  /**
   * For a CUSTOMER user with exactly one tenant: limit them to these sites
   * (a "site contact"). Omit / empty = access to the whole customer.
   */
  siteIds: z.array(z.guid()).max(500).optional(),
  /**
   * ENGINEER/PROJECT_MANAGER only - explicit MSP-branding override, used
   * only as a fallback when the user's own email domain doesn't match any
   * MSP's domain list. See Me.mspBranding / auth.service.ts's resolveMspBranding.
   */
  mspId: z.guid().nullable().optional(),
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

/** The only field an existing user's MSP override can be changed through - see PATCH /users/:id/msp. */
export const updateUserMspSchema = z.object({ mspId: z.guid().nullable() }).strict();
export type UpdateUserMspInput = z.infer<typeof updateUserMspSchema>;

export const inviteUserSchema = z.object({
  email: emailSchema,
  displayName: z.string().min(1).max(120),
  role: z.enum(ROLES),
  tenantId: z.guid().optional(),
});

export const createTenantSchema = z.object({
  name: z.string().min(2).max(120),
  slug: z
    .string()
    .min(2)
    .max(40)
    .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/, 'lowercase letters, numbers and hyphens'),
  primaryDomain: z.string().max(253).optional(),
});
export type CreateTenantInput = z.infer<typeof createTenantSchema>;

/** Toggles the per-customer safeguard that blocks all writes to the customer's live Microsoft Teams tenant - see docs/SECURITY.md. */
export const updateTenantSchema = z.object({ teamsReadOnly: z.boolean() }).strict();
export type UpdateTenantInput = z.infer<typeof updateTenantSchema>;

/** Logo upload is a separate multipart route - this is just the color half of branding. */
export const updateTenantBrandingSchema = z
  .object({ accentColor: z.string().regex(HEX_COLOR_RE, 'must be a #rrggbb hex color') })
  .strict();
export type UpdateTenantBrandingInput = z.infer<typeof updateTenantBrandingSchema>;

/** A single email domain, lowercased/trimmed - server-side dedupe happens in MspsService. */
const mspDomainSchema = z
  .string()
  .trim()
  .min(1)
  .max(253)
  .transform((s) => s.toLowerCase().replace(/^@/, ''));

export const createMspSchema = z.object({
  name: z.string().min(2).max(120),
  slug: z
    .string()
    .min(2)
    .max(40)
    .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/, 'lowercase letters, numbers and hyphens'),
  domains: z.array(mspDomainSchema).max(50).optional(),
});
export type CreateMspInput = z.infer<typeof createMspSchema>;

export const updateMspSchema = z
  .object({
    name: z.string().min(2).max(120).optional(),
    domains: z.array(mspDomainSchema).max(50).optional(),
  })
  .strict();
export type UpdateMspInput = z.infer<typeof updateMspSchema>;

/** Same shape as updateTenantBrandingSchema - logo upload is a separate multipart route. */
export const updateMspBrandingSchema = z
  .object({ accentColor: z.string().regex(HEX_COLOR_RE, 'must be a #rrggbb hex color') })
  .strict();
export type UpdateMspBrandingInput = z.infer<typeof updateMspBrandingSchema>;

export const addMembershipSchema = z.object({
  userId: z.guid(),
  /** Limit a CUSTOMER member to these site ids; omit / empty = whole customer. */
  siteIds: z.array(z.guid()).max(500).optional(),
});

/** Change an existing member's site scope. Empty array = whole customer. */
export const updateMembershipSchema = z.object({
  siteIds: z.array(z.guid()).max(500),
});

export const startConnectionSchema = z.object({
  tenantDomain: z
    .string()
    .max(253)
    .regex(/^[a-z0-9.-]+$/i, 'e.g. contoso.onmicrosoft.com')
    .optional(),
});

export const createDeploymentSchema = z.object({
  connectionId: z.guid(),
  mode: z.enum(['dry_run', 'execute']),
  scope: z
    .object({
      /** Every deployment run acts on one site's Build rows at a time. */
      siteId: z.guid(),
      // 'm365_groups' deliberately excluded - apps/worker/src/main.ts's
      // handleDeploymentRun has no `scope.sheets.includes('m365_groups')`
      // branch at all (build_m365_groups has no worker wiring yet), so a
      // request naming it ran to completion doing nothing for that sheet
      // while reporting success - confirmed this session. Matches
      // deploymentPreviewQuerySchema's own already-correct exclusion below.
      sheets: z.array(z.enum(DEPLOYMENT_SHEETS)).min(1),
      waves: z.array(z.string()).optional(),
      rowIds: z.array(z.guid()).optional(),
    })
    .strict(),
  /** Scheduled cutover: start a live run at this time instead of now (scheduledRunProblem says what is allowed). */
  runAt: z.iso.datetime({ offset: true }).optional(),
});
export type CreateDeploymentInput = z.infer<typeof createDeploymentSchema>;

/**
 * Express's default query parser turns `?sheets=a,b,c` into the single
 * string "a,b,c", not an array - only repeated keys (`?sheets=a&sheets=b`)
 * parse as an array. Query-string array params need to accept either shape;
 * `arraySchema` is the fully-built target schema (with its own .min()/etc).
 */
const queryArray = <T extends z.ZodTypeAny>(arraySchema: T) =>
  z.preprocess((v) => (typeof v === 'string' ? v.split(',') : v), arraySchema);

/**
 * Read-only "what would this deploy right now" preview - no connection, no
 * worker/queue involvement. Deliberately excludes m365_groups: no worker code
 * path handles that sheet yet (build_m365_groups has no CRUD/API wiring at
 * all - see the "reverse-engineer OVP012" plan). call_queues/auto_attendants
 * are planned (planCallQueueRow/planAutoAttendantRow) but not in the default
 * list, matching resource_accounts' own opt-in-by-caller shape.
 */
/** GET deployments/drift - one site's drift, worked out fresh from the stored live state. */
export const deploymentDriftQuerySchema = z.object({ siteId: z.guid() });
export type DeploymentDriftQuery = z.infer<typeof deploymentDriftQuerySchema>;

/** POST build/drift/accept - take what Teams has now into the design for one drifted row. */
export const buildDriftAcceptSchema = z
  .object({
    object_type: z.enum(['user', 'cap', 'auto_attendant', 'call_queue']),
    row_id: z.guid(),
  })
  .strict();
export type BuildDriftAcceptInput = z.infer<typeof buildDriftAcceptSchema>;

export const deploymentPreviewQuerySchema = z.object({
  siteId: z.guid(),
  sheets: queryArray(z.array(z.enum(DEPLOYMENT_SHEETS)).min(1)).default([
    'users',
    'caps',
    'resource_accounts',
  ]),
  rowIds: queryArray(z.array(z.guid())).optional(),
});
export type DeploymentPreviewQuery = z.infer<typeof deploymentPreviewQuerySchema>;

/**
 * GET /deployments' own query - `siteId` scopes the site workspace's Run
 * history to that site before the service's own `limit(50)` applies.
 * Omitting it keeps the old tenant-wide behaviour for any other caller.
 * Without this, a busy tenant's `limit(50)` was taken tenant-wide first,
 * so a quieter site's own history could be pushed out of the window
 * entirely - confirmed this session.
 */
export const listDeploymentsQuerySchema = z.object({
  siteId: z.guid().optional(),
});
export type ListDeploymentsQuery = z.infer<typeof listDeploymentsQuerySchema>;

/** rowIds omitted = generate for the whole site. */
export const generateDeploymentDocumentSchema = z.object({
  rowIds: z.array(z.guid()).optional(),
});
export type GenerateDeploymentDocumentInput = z.infer<typeof generateDeploymentDocumentSchema>;

/* ------------------------- Site Data DTOs ------------------------- */

const str = (max = 400) => z.string().trim().max(max);
const optStr = (max = 400) => str(max).optional().or(z.literal(''));

/**
 * Shared shape for every "bulk edit N selected rows" action (Design &
 * Build's Users/CAPs, Site Data's Users/CAPs, …): a bounded list of
 * row ids plus a strict partial patch over just the fields that entity
 * allows to bulk-set. One definition of the id-list bound, so raising or
 * lowering how many rows a bulk edit can touch at once changes for every
 * module together.
 */
/**
 * Site Data bulk delete (users / CAPs / resource accounts): either the
 * rows ticked in the grid, or every row of that kind on one site ("start
 * again"). Each row is deleted exactly as a single delete would be.
 */
export const discoveryBulkDeleteSchema = z.union([
  z.object({ ids: z.array(z.guid()).min(1).max(5000) }).strict(),
  z.object({ site_id: z.guid(), all: z.literal(true) }).strict(),
]);
export type DiscoveryBulkDeleteInput = z.infer<typeof discoveryBulkDeleteSchema>;

const bulkPatchSchema = <T extends z.ZodRawShape>(fields: T) =>
  z
    .object({
      ids: z.array(z.guid()).min(1).max(500),
      patch: z.object(fields).strict(),
    })
    .strict();

/**
 * Optional link to a `discovery_sites.id`. A cleared dropdown sends '' -> null.
 * The API requires this to be set (and in scope) for site-scoped "site contact"
 * users; for whole-customer users it may be left blank.
 */
const siteIdRef = z.preprocess(
  (v) => (v === '' || v == null ? null : v),
  z.guid().nullable().optional(),
);

/** A site's delivery region: AMER, EMEA or APAC ('' clears it). */
export const siteRegionSchema = z.enum(SITE_REGIONS).or(z.literal(''));

export const discoveryGeneralSchema = z
  .object({
    migrationId: optStr(80),
    region: optStr(120),
    author: optStr(160),
    licensingModel: z.enum(LICENSING_MODELS).or(z.literal('')).optional(),
    targetGoLive: optStr(40),
    primaryContactEmail: z.string().trim().max(320).email().or(z.literal('')).optional(),
    notes: optStr(4000),
  })
  .strict();
export type DiscoveryGeneralInput = z.infer<typeof discoveryGeneralSchema>;

/** One person in a role on a site: a picked user, or free text. */
const siteContactSchema = z
  .object({
    userId: z.guid().optional(),
    name: z.string().trim().max(160).optional(),
    email: z.string().trim().max(320).email().or(z.literal('')).optional(),
  })
  .strict()
  .refine((c) => !!(c.userId || c.name || c.email), 'Pick a user, or type a name or an email.');
const siteContactList = z.array(siteContactSchema).max(MAX_CONTACTS_PER_ROLE).optional();
export const siteContactsSchema = z
  .object({
    siteContacts: siteContactList,
    providerPm: siteContactList,
    providerEngineer: siteContactList,
    providerPortingPm: siteContactList,
    providerPortingEngineer: siteContactList,
  })
  .strict();

/**
 * Per-site overview (discovery_sites.overview): the old customer-level overview fields, the delivery region and the people on the site by role.
 * Only SUPER_ADMIN / PROJECT_MANAGER / ENGINEER may write it.
 */
export const discoverySiteOverviewSchema = discoveryGeneralSchema
  .omit({ primaryContactEmail: true })
  .extend({
    /** The site's delivery region. Stored on discovery_sites.region (the one place it lives), not in the overview JSON. */
    region: siteRegionSchema.optional(),
    contacts: siteContactsSchema.optional(),
    /** Days between number-port document reminder emails for this site (default 7). */
    portDocReminderDays: z.coerce.number().int().min(1).max(90).optional(),
  })
  .strict();
export type DiscoverySiteOverviewInput = z.infer<typeof discoverySiteOverviewSchema>;

/** A site's unique key. Alnum, dash, dot, underscore. */
export const sitecodeSchema = z
  .string()
  .trim()
  .min(1)
  .max(60)
  .regex(/^[A-Za-z0-9._-]+$/, 'letters, digits, dot, dash or underscore only');

const latitude = z.preprocess(
  (v) => (v === '' || v == null ? null : v),
  z.coerce.number().min(-90).max(90).nullable().optional(),
);
const longitude = z.preprocess(
  (v) => (v === '' || v == null ? null : v),
  z.coerce.number().min(-180).max(180).nullable().optional(),
);

/** Another address on a site (a second building on a campus): shown on the map under the site's code. */
export const siteAddressSchema = z
  .object({
    label: optStr(120),
    address: z.string().trim().min(1, 'enter the address').max(500),
    latitude,
    longitude,
  })
  .strict();
export type SiteAddressInput = z.infer<typeof siteAddressSchema>;
export const MAX_SITE_ADDRESSES = 50;

export const discoverySiteSchema = z
  .object({
    sitecode: sitecodeSchema,
    name: optStr(200),
    address: optStr(500),
    country: optStr(80),
    region: siteRegionSchema.optional(),
    latitude,
    longitude,
    /** the site's other addresses; when sent, this list replaces the saved one */
    additionalAddresses: z.array(siteAddressSchema).max(MAX_SITE_ADDRESSES).optional(),
    paging: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();
export type DiscoverySiteInput = z.infer<typeof discoverySiteSchema>;

const e164ish = z
  .string()
  .trim()
  .min(3)
  .max(20)
  .regex(/^\+?[0-9]+$/, 'digits only, optional leading +');

export const discoveryNumberRangeSchema = z
  .object({
    sitecode: sitecodeSchema,
    range_start: e164ish,
    range_end: e164ish,
    kind: z.enum(NUMBER_RANGE_KINDS),
    carrier: optStr(160),
    loa_sent: z.coerce.boolean().optional(),
    loa_completed: z.coerce.boolean().optional(),
    comments: optStr(2000),
    /** a port-in range: the carrier the numbers are moving to (`carrier` is where they are now) */
    new_carrier: optStr(160),
  })
  .strict();
export type DiscoveryNumberRangeInput = z.infer<typeof discoveryNumberRangeSchema>;

/** Bulk-create number ranges for one site from a spreadsheet (columns DID-START, DID-END). */
export const numberRangesImportSchema = z
  .object({
    site_id: z.guid(),
    kind: z.enum(NUMBER_RANGE_KINDS),
    carrier: optStr(160),
    /** for a port-in: the carrier the numbers are moving to */
    new_carrier: optStr(160),
    rows: z
      .array(z.object({ range_start: e164ish, range_end: e164ish }).strict())
      .min(1)
      .max(500),
  })
  .strict();
export type NumberRangesImportInput = z.infer<typeof numberRangesImportSchema>;

/* --------------------- Number-port document collection --------------------- */
// "LOA Site Data and Tracking" - a number-port request's document
// checklist. See docs on the three-tier model: port_document_types (global
// catalog) -> site_port_document_types (per-site enabled subset) ->
// number_port_request_items (per-request checklist, picked from the site's
// enabled subset).

/** Global catalog entry - admin/PM/Engineer only (discovery:sites:manage). */
export const portDocumentTypeSchema = z
  .object({
    key: str(60).regex(/^[a-z][a-z0-9_]*$/, 'lowercase letters, digits and underscores only'),
    label: str(160),
    ordinal: z.coerce.number().int().min(0).max(1000).optional(),
    active: z.boolean().optional(),
  })
  .strict();
export type PortDocumentTypeInput = z.infer<typeof portDocumentTypeSchema>;

/** Toggle one catalog entry on/off for a site. */
export const sitePortDocumentTypeSchema = z
  .object({
    document_type_id: z.guid(),
    enabled: z.boolean(),
  })
  .strict();
export type SitePortDocumentTypeInput = z.infer<typeof sitePortDocumentTypeSchema>;

/**
 * PM/Engineer builds a request's checklist by replacing its whole item set in
 * one call - picked from the site's enabled catalog subset, each optionally
 * carrying a note (e.g. "authorized signer: Jane Doe" for the ID item).
 */
export const numberPortRequestItemsSchema = z
  .object({
    items: z
      .array(
        z.object({
          document_type_id: z.guid(),
          note: optStr(500),
        }),
      )
      .max(50),
  })
  .strict();
export type NumberPortRequestItemsInput = z.infer<typeof numberPortRequestItemsSchema>;

/** PM/Engineer marks one uploaded item as no good and asks the customer to redo it. */
export const numberPortItemRejectSchema = z
  .object({
    reason: str(500),
  })
  .strict();
export type NumberPortItemRejectInput = z.infer<typeof numberPortItemRejectSchema>;

/** PM/Engineer marks one item as not needed after all, without the customer uploading anything. */
export const numberPortItemWaiveSchema = z
  .object({
    waived: z.boolean(),
  })
  .strict();
export type NumberPortItemWaiveInput = z.infer<typeof numberPortItemWaiveSchema>;

export const discoveryNetworkSchema = z
  .object({
    site_id: siteIdRef,
    scope: z.enum(NETWORK_SCOPES),
    subnet: str(64).min(1),
    mask: z.preprocess(
      (v) => (v === '' || v == null ? null : v),
      z.coerce.number().int().min(0).max(32).nullable().optional(),
    ),
    /** kept permissive so legacy free-text rows still save; the UI offers NETWORK_LOCATIONS */
    location: optStr(200),
    vlan_id: z.preprocess(
      (v) => (v === '' || v == null ? null : v),
      z.coerce.number().int().min(1).max(4094).nullable().optional(),
    ),
    network_type: z.preprocess(
      (v) => (v === '' ? null : v),
      z.enum(NETWORK_TYPES).nullable().optional(),
    ),
  })
  .strict();
export type DiscoveryNetworkInput = z.infer<typeof discoveryNetworkSchema>;

export const discoveryFlowSchema = z
  .object({
    site_id: siteIdRef,
    kind: z.enum(FLOW_KINDS),
    name: str(200).min(1),
    description: optStr(8000),
    /**
     * The AA/CQ creation wizard's structured capture (see wizard.ts) - set
     * once, by the wizard's own "Save" step, never hand-edited. Absent/null
     * means this row was created the old freeform way (Source: Manual in
     * the Call flows tab). Not cross-checked against `kind` here (a
     * mismatch can't happen from the wizard's own UI, which always POSTs
     * both together) - same "trust the client that built the payload"
     * tradeoff `callQueueActionSchema`'s `target` already makes.
     */
    wizard_answers: z.union([autoAttendantWizardAnswersSchema, callQueueWizardAnswersSchema]).optional(),
    wizard_version: z.number().int().min(1).optional(),
    // Inlined rather than using the `refId`/`blankToNull` helpers below -
    // both are `const`s declared later in this file, and this schema is
    // built (and so evaluated) earlier, so referencing them here would hit
    // the temporal dead zone at module load.
    /** discovery_voice_apps.id - the voice app this Call flow is the capture of. Made for the flow when it is saved without one. */
    voice_app_id: z.preprocess((v) => (v === '' ? null : v), z.guid().nullable().optional()),
    /** discovery_resource_accounts.id - the resource account that answers this, if already known: it joins the flow's voice app. */
    resource_account_id: z.preprocess((v) => (v === '' ? null : v), z.guid().nullable().optional()),
  })
  .strict();
export type DiscoveryFlowInput = z.infer<typeof discoveryFlowSchema>;

/* ------------------- Telephony discovery DTOs ------------------- */

const yn = z.coerce.boolean();

export const callingPolicySchema = z
  .object({
    name: str(120).min(1),
    description: optStr(500),
    allow_local: yn.optional(),
    allow_national: yn.optional(),
    allow_international: yn.optional(),
    allow_service: yn.optional(),
    allow_premium: yn.optional(),
  })
  .strict();
export type CallingPolicyInput = z.infer<typeof callingPolicySchema>;

const blankToNull = <T extends z.ZodTypeAny>(inner: T) =>
  z.preprocess((v) => (v === '' ? null : v), inner.nullable().optional());

const callerId = blankToNull(z.enum(CALLER_ID_OPTIONS));
/** '' from a cleared dropdown is treated as null. */
const refId = blankToNull(z.guid());
/**
 * Voicemail language, stored as the culture code Teams' PromptLanguage wants.
 * Accepts a code in any case or a common English name (see
 * normalizeVoicemailLanguage) so Excel imports and old free-text values still
 * land; anything unrecognised is rejected rather than stored undeployable.
 */
const voicemailLanguage = z.preprocess(
  (v) => (v == null || v === '' ? null : (normalizeVoicemailLanguage(v) ?? v)),
  z
    .enum(VOICEMAIL_PROMPT_LANGUAGE_CODES, {
      error: () => 'Voicemail language must be one Teams supports, e.g. en-US or "English (United Kingdom)"',
    })
    .nullable()
    .optional(),
);

export const discoveryUserSchema = z
  .object({
    site_id: siteIdRef,
    upn: upnSchema,
    display_name: optStr(160),
    calling_policy_id: refId,
    caller_id: callerId,
    voicemail_enabled: yn.optional(),
    voicemail_language: voicemailLanguage,
    requires_handset: yn.optional(),
    handset_model: optStr(120),
    access_port_id: optStr(80),
    comments: optStr(2000),
    /** number the customer asked for; free text, reconciled in Design & Build */
    requested_number: optStr(40),
    /** assign / move / clear this holder's number in the same call */
    phone_number_id: refId,
  })
  .strict();
export type DiscoveryUserInput = z.infer<typeof discoveryUserSchema>;

/* ---------- Import from Excel: bulk-create users from a spreadsheet ---------- */

/** Tolerant yes/no/true/1/x -> boolean; anything else -> undefined. */
const ynLoose = z.preprocess((v) => {
  if (typeof v === 'boolean') return v;
  const t = String(v ?? '').trim().toLowerCase();
  if (['y', 'yes', 'true', '1', 'x', 'enabled', 'on'].includes(t)) return true;
  if (['n', 'no', 'false', '0', 'disabled', 'off', ''].includes(t)) return false;
  return undefined;
}, z.boolean().optional());

const callerIdLoose = z.preprocess(
  (v) => {
    const t = String(v ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');
    return t === '' ? undefined : t;
  },
  z.enum(CALLER_ID_OPTIONS).optional(),
);

/** One parsed spreadsheet row. `calling_policy` is a policy *name* (resolved
 * server-side); `requested_number` is free text. */
export const importUserRowSchema = z
  .object({
    upn: upnSchema,
    requested_number: str(40).optional().or(z.literal('')),
    display_name: optStr(160),
    calling_policy: optStr(120),
    caller_id: callerIdLoose,
    voicemail_enabled: ynLoose,
    voicemail_language: voicemailLanguage,
    requires_handset: ynLoose,
    handset_model: optStr(120),
    access_port_id: optStr(80),
    comments: optStr(2000),
  })
  .strict();
export type ImportUserRow = z.infer<typeof importUserRowSchema>;

export const usersImportSchema = z
  .object({
    site_id: z.guid(),
    rows: z.array(importUserRowSchema).min(1).max(2000),
  })
  .strict();
export type UsersImportInput = z.infer<typeof usersImportSchema>;

export const discoveryCapSchema = z
  .object({
    site_id: siteIdRef,
    display_name: str(160).min(1),
    upn: optStr(320),
    device_model: optStr(120),
    calling_policy_id: refId,
    caller_id: callerId,
    access_port_id: optStr(80),
    comments: optStr(2000),
    phone_number_id: refId,
  })
  .strict();
export type DiscoveryCapInput = z.infer<typeof discoveryCapSchema>;

/**
 * Bulk-edit for discovery_users/discovery_caps - the same patch applied to
 * many rows at once, the Site Data counterpart to
 * buildBulkPatchSchema above (both built on the shared bulkPatchSchema).
 * `site_id`, `upn`/`display_name` and `phone_number_id` are excluded for the
 * same reason as Design & Build's: they're per-row-unique identifiers, so
 * bulk-setting the same value on every selected row would be destructive
 * (moving everyone to one site, giving them all the same name, or claiming
 * the same phone number for each of them) rather than a real bulk edit.
 * Users additionally excludes `requested_number` - a customer's requested
 * number is specific to that one person.
 */
const { site_id: _bulkUserSiteId, upn: _bulkUserUpn, display_name: _bulkUserName, requested_number: _bulkUserReqNum, phone_number_id: _bulkUserPhoneId, ...discoveryUserBulkWritable } = discoveryUserSchema.shape;
export const discoveryUserBulkPatchSchema = bulkPatchSchema(discoveryUserBulkWritable);
export type DiscoveryUserBulkPatchInput = z.infer<typeof discoveryUserBulkPatchSchema>;

const { site_id: _bulkCapSiteId, upn: _bulkCapUpn, display_name: _bulkCapName, phone_number_id: _bulkCapPhoneId, ...discoveryCapBulkWritable } = discoveryCapSchema.shape;
export const discoveryCapBulkPatchSchema = bulkPatchSchema(discoveryCapBulkWritable);
export type DiscoveryCapBulkPatchInput = z.infer<typeof discoveryCapBulkPatchSchema>;

/** A voice app (auto attendant or call queue) as Site Data captures it: its name, kind and the plain-words detail of how it behaves. */
export const discoveryVoiceAppSchema = z
  .object({
    site_id: siteIdRef,
    name: str(160).min(1),
    kind: z.enum(RESOURCE_ACCOUNT_KINDS),
    directory_entry: optStr(160),
    business_hours: optStr(400),
    who_answers: optStr(2000),
    ooh_action: optStr(1000),
    exception_conditions: optStr(2000),
    exception_action: optStr(1000),
    holiday: optStr(1000),
    advanced_features: optStr(4000),
    comments: optStr(2000),
    /** Add only: a free number of the site. A resource account named like the voice app is made to hold it and answers for the app. */
    phone_number_id: z.preprocess((v) => (v === '' ? null : v), z.guid().nullable().optional()),
  })
  .strict();
export type DiscoveryVoiceAppInput = z.infer<typeof discoveryVoiceAppSchema>;

/** A resource account: its own name, UPN (once it exists) and phone number(s); it answers for one voice app, or none yet. */
export const discoveryResourceAccountSchema = z
  .object({
    site_id: siteIdRef,
    name: str(160).min(1),
    kind: z.enum(RESOURCE_ACCOUNT_KINDS),
    upn: optStr(320),
    voice_app_id: z.preprocess((v) => (v === '' ? null : v), z.guid().nullable().optional()),
    comments: optStr(2000),
  })
  .strict();
export type DiscoveryResourceAccountInput = z.infer<typeof discoveryResourceAccountSchema>;

/** Attach / detach a number from a resource account (which can hold several). */
export const resourceAccountNumberSchema = z
  .object({ phone_number_id: z.guid() })
  .strict();

/** Manually flip a free number to/from 'reserved' (e.g. held for porting). */
export const numberReserveSchema = z.object({ reserved: z.boolean() }).strict();
/** PATCH numbers/:id/park - park a spare number, or bring a parked one back into use (project mode only; see number-parking.ts). */
export const numberParkSchema = z.object({ parked: z.boolean() }).strict();
/** POST numbers/:id/end-cooloff - reuse a released number before its cool-off ends; the reason is audited. */
export const numberEndCooloffSchema = z.object({ reason: z.string().trim().min(3, 'Say why the number must be reused now').max(500) }).strict();
export type NumberEndCooloffInput = z.infer<typeof numberEndCooloffSchema>;
/** PATCH number-policy - the customer's cool-off period in days; 0 turns it off. */
export const numberPolicyPatchSchema = z.object({ cooloff_days: z.number().int().min(0).max(3650) }).strict();
export type NumberPolicyPatchInput = z.infer<typeof numberPolicyPatchSchema>;

/**
 * Link every not-yet-linked Site Data user for a site to the tenant user
 * with the same UPN (from the last Discovery run). Idempotent.
 */
export const relinkUsersSchema = z.object({ site_id: z.guid() }).strict();
export type RelinkUsersInput = z.infer<typeof relinkUsersSchema>;

/* ------------------- Site Data list queries ------------------- */

/**
 * Query string for the paginated Site Data list endpoints
 * (`GET .../users`, `.../numbers`, etc). Everything is optional; `page`/`limit`
 * default. `siteId` narrows to one site (ignored/clamped for site contacts).
 */
export const discoveryListQuerySchema = z.object({
  siteId: z.guid().optional(),
  q: z.string().trim().max(160).optional(),
  status: z.enum(NUMBER_STATUSES).optional(),
  page: z.coerce.number().int().min(1).max(100000).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type DiscoveryListQuery = z.infer<typeof discoveryListQuerySchema>;

/* -------------------------- Feature requests -------------------------- */

const featureText = (min: number, max = 6000) => z.string().trim().min(min).max(max);

/**
 * A new feature request. The long fields feed the "prompt for Claude" the board
 * generates, so the client guidance nudges towards Claude prompting best
 * practice: state the goal and the pain, describe the desired end state, give a
 * concrete example, list acceptance criteria, and call out anything off-limits.
 */
export const featureRequestCreateSchema = z
  .object({
    title: z.string().trim().min(6, 'Give a short, specific title').max(160),
    area: z.enum(FEATURE_AREAS),
    priority: z.enum(FEATURE_PRIORITIES).default('medium'),
    problem: featureText(20),
    proposal: featureText(20),
    current_behavior: optStr(6000),
    examples: optStr(6000),
    acceptance: optStr(6000),
    constraints: optStr(6000),
    affected_roles: z.array(z.enum(ROLES)).max(ROLES.length).default([]),
  })
  .strict();
export type FeatureRequestCreateInput = z.infer<typeof featureRequestCreateSchema>;

/** Board edits: any field, plus the workflow `status` and a `decision_note`. */
export const featureRequestUpdateSchema = z
  .object({
    title: z.string().trim().min(6).max(160).optional(),
    area: z.enum(FEATURE_AREAS).optional(),
    priority: z.enum(FEATURE_PRIORITIES).optional(),
    status: z.enum(FEATURE_STATUSES).optional(),
    problem: featureText(20).optional(),
    proposal: featureText(20).optional(),
    current_behavior: optStr(6000),
    examples: optStr(6000),
    acceptance: optStr(6000),
    constraints: optStr(6000),
    affected_roles: z.array(z.enum(ROLES)).max(ROLES.length).optional(),
    decision_note: optStr(2000),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export type FeatureRequestUpdateInput = z.infer<typeof featureRequestUpdateSchema>;

/* ---------------------------- Bug reports ---------------------------- */
// Reuses FEATURE_AREAS for `area` - "which part of the platform" is the same
// question for a bug as for a feature request, no need for a second list.

const bugText = (min: number, max = 6000) => z.string().trim().min(min).max(max);

export const bugReportCreateSchema = z
  .object({
    title: z.string().trim().min(6, 'Give a short, specific title').max(160),
    area: z.enum(FEATURE_AREAS),
    severity: z.enum(BUG_SEVERITIES).default('medium'),
    steps_to_reproduce: bugText(10),
    expected_behavior: bugText(5),
    actual_behavior: bugText(5),
    affected_customer: optStr(160),
    environment: optStr(500),
  })
  .strict();
export type BugReportCreateInput = z.infer<typeof bugReportCreateSchema>;

/** Board edits: any field, plus the workflow `status` and a `resolution_note`. */
export const bugReportUpdateSchema = z
  .object({
    title: z.string().trim().min(6).max(160).optional(),
    area: z.enum(FEATURE_AREAS).optional(),
    severity: z.enum(BUG_SEVERITIES).optional(),
    status: z.enum(BUG_STATUSES).optional(),
    steps_to_reproduce: bugText(10).optional(),
    expected_behavior: bugText(5).optional(),
    actual_behavior: bugText(5).optional(),
    affected_customer: optStr(160),
    environment: optStr(500),
    resolution_note: optStr(2000),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export type BugReportUpdateInput = z.infer<typeof bugReportUpdateSchema>;

/* ------------------ Discovery (live customer-tenant inventory) ------------------ */

/**
 * Kick off a discovery run over an `active` tenant connection. `scopeTypes`
 * limits the run to part of the tenant (a whole step's types, or individual
 * ones); omit it for a full discovery.
 */
export const tenantDiscoveryStartSchema = z
  .object({
    connectionId: z.guid(),
    scopeTypes: z.array(z.enum(TENANT_OBJECT_TYPES)).min(1).max(TENANT_OBJECT_TYPES.length).optional(),
    /** also sync AccountEnabled = false accounts (default: skip them) */
    includeDisabled: z.boolean().optional(),
    /** also sync Guest / IneligibleUser and users not licensed for Teams (default: skip) */
    includeUnlicensed: z.boolean().optional(),
  })
  .strict();
export type TenantDiscoveryStartInput = z.infer<typeof tenantDiscoveryStartSchema>;

/** Per-customer Discovery settings (`PATCH .../tenant-discovery/settings`). */
export const tenantDiscoverySettingsSchema = z
  .object({
    /** when true (default), runs store only User accounts licensed for Teams */
    filterUsers: z.boolean().optional(),
    /** when true (default), email the person who started a run when it finishes */
    notifyOnComplete: z.boolean().optional(),
    /** when true (default false), after each full run add objects whose number is in a site's range to that site's Site Data */
    autoAddByNumberRange: z.boolean().optional(),
  })
  .strict()
  .refine((v) => v.filterUsers !== undefined || v.notifyOnComplete !== undefined || v.autoAddByNumberRange !== undefined, {
    message: 'provide at least one setting to update',
  });
export type TenantDiscoverySettingsInput = z.infer<typeof tenantDiscoverySettingsSchema>;

/** Query string for the paginated inventory lists (`GET .../objects`, `.../users`). */
export const tenantObjectsQuerySchema = z.object({
  type: z.enum(TENANT_OBJECT_TYPES).optional(),
  policyType: z.enum(TENANT_POLICY_TYPES).optional(),
  q: z.string().trim().max(160).optional(),
  includeRemoved: z
    .preprocess((v) => v === 'true' || v === true, z.boolean())
    .optional(),
  /** users only: Enterprise-Voice-enabled accounts only */
  ev: z.preprocess((v) => v === 'true' || v === true, z.boolean()).optional(),
  /** users only: Get-CsOnlineUser AccountType (User, ResourceAccount, Guest, IneligibleUser, SfBOnPremUser) */
  accountType: z.string().trim().max(40).optional(),
  page: z.coerce.number().int().min(1).max(100000).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type TenantObjectsQuery = z.infer<typeof tenantObjectsQuerySchema>;

/**
 * Query string for `GET .../tenant-discovery/groups` - drives both
 * GroupAutocomplete's Shared Voicemail groupId typeahead (small `limit`,
 * `page` defaulted) and Discovery's paginated "M365 groups" tab.
 */
export const tenantGroupsQuerySchema = z.object({
  q: z.string().trim().max(160).optional(),
  page: z.coerce.number().int().min(1).max(100000).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type TenantGroupsQuery = z.infer<typeof tenantGroupsQuerySchema>;

/** Exact-UPN lookup used by the Site Data add-user autofill. */
/** GET discovery/users/compare - two discovered users (tenant_users.id) side by side. */
export const tenantUserCompareSchema = z.object({ a: z.guid(), b: z.guid() });
export type TenantUserCompareQuery = z.infer<typeof tenantUserCompareSchema>;

export const tenantUserLookupSchema = z.object({
  upn: z.string().trim().min(3).max(320),
});
export type TenantUserLookupQuery = z.infer<typeof tenantUserLookupSchema>;

/** One candidate's chosen (or overridden) destination site for an import. */
const importAssignmentSchema = z.object({ id: z.guid(), siteId: z.guid() }).strict();

/**
 * Bulk-create Site Data users from the discovered tenant users that are
 * not already captured (matched on lower(upn)). `assignments` gives each
 * included candidate its own site (from the import dialog's per-row
 * suggestion/override); `siteId` is an optional fallback applied to any
 * candidate not named in `assignments` (the "set all to..." convenience -
 * omit `assignments` entirely and every candidate goes to `siteId`, same as
 * this endpoint's original whole-batch-one-site behaviour).
 */
export const tenantUsersImportSchema = z
  .object({
    siteId: z.guid().optional(),
    assignments: z.array(importAssignmentSchema).max(2000).default([]),
    onlyEnterpriseVoice: z.boolean().default(true),
    calling_policy_id: z.guid().nullable().optional(),
  })
  .strict();
export type TenantUsersImportInput = z.infer<typeof tenantUsersImportSchema>;

/** Same shape as `tenantUsersImportSchema`, for discovered resource accounts. */
export const tenantResourceAccountsImportSchema = z
  .object({
    siteId: z.guid().optional(),
    assignments: z.array(importAssignmentSchema).max(2000).default([]),
  })
  .strict();
export type TenantResourceAccountsImportInput = z.infer<typeof tenantResourceAccountsImportSchema>;

/**
 * Add selected discovered objects to a site, in Site Data and (optionally) Design & Build. Each item names the exact
 * object - a tenant_users id for a user or common area phone account, a tenant_objects id for resource accounts, auto attendants
 * and call queues - never a display name, which can repeat.
 */
export const ADD_FROM_DISCOVERY_KINDS = ['account', 'resource_account', 'call_queue', 'auto_attendant'] as const;
export const addFromDiscoverySchema = z
  .object({
    kind: z.enum(ADD_FROM_DISCOVERY_KINDS),
    // `as` only applies to an account: routed to Users or Common area phones. Left out, it is detected from the account's licence plan.
    items: z.array(z.object({ id: z.guid(), siteId: z.guid(), as: z.enum(['user', 'cap']).optional() }).strict()).min(1).max(500),
    alsoBuild: z.boolean().default(true),
  })
  .strict();
export type AddFromDiscoveryInput = z.infer<typeof addFromDiscoverySchema>;

/* ------------------------- Design & Build DTOs ------------------------- */
/* Every build_* row is reached through a site workspace (see docs/DATA-MODEL.md). */

const policyMap = z.record(z.string(), z.string().nullable()).optional();
/** Picker submits a tenant_policies.id per PolicyKey - see BuildService.resolvePolicyIds. */
const policyIdMap = z.record(z.string(), z.guid().nullable()).optional();
/** RecordDialog's `type: 'select'` sends '' for an untouched/cleared dropdown - treat as null, not a validation error. */
const numberType = blankToNull(z.enum(NUMBER_TYPES));

/**
 * Structured shape for build_users/build_caps' call_forwarding jsonb -
 * mirrors Set-CsUserCallingSettings' Forwarding/Unanswered/BusyOnBusy
 * settings groups exactly (see planIdentityRow in deployment.ts).
 */
const callForwardingSchema = z
  .object({
    forwarding: z
      .object({
        enabled: z.boolean(),
        type: z.enum(CALL_FORWARDING_TYPES).optional(),
        targetType: z.enum(CALL_TARGET_TYPES).optional(),
        target: optStr(200),
      })
      .strict()
      .optional(),
    unanswered: z
      .object({
        enabled: z.boolean(),
        // seconds, per Learn's allowed range/increments (5-60s)
        delaySeconds: z.number().int().min(5).max(60).optional(),
        targetType: z.enum(CALL_TARGET_TYPES).optional(),
        target: optStr(200),
      })
      .strict()
      .optional(),
    busyOnBusy: z.enum(BUSY_ON_BUSY_OPTIONS).optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    // Set-CsUserCallingSettings' -IsForwardingEnabled/-IsUnansweredEnabled
    // don't require a target - planIdentityRow just omits ForwardingType/
    // Target when they're unset, so `enabled: true` with nothing else
    // silently deployed "forwarding on, no destination" (confirmed this
    // session). Reject that combination here instead of at deploy time.
    if (val.forwarding?.enabled) {
      if (!val.forwarding.type) ctx.addIssue({ code: 'custom', path: ['forwarding', 'type'], message: 'Required when forwarding is enabled' });
      if (!val.forwarding.targetType) ctx.addIssue({ code: 'custom', path: ['forwarding', 'targetType'], message: 'Required when forwarding is enabled' });
      if (val.forwarding.targetType === 'SingleTarget' && !val.forwarding.target) {
        ctx.addIssue({ code: 'custom', path: ['forwarding', 'target'], message: 'Required for a single target' });
      }
    }
    if (val.unanswered?.enabled) {
      if (!val.unanswered.targetType) ctx.addIssue({ code: 'custom', path: ['unanswered', 'targetType'], message: 'Required when unanswered forwarding is enabled' });
      if (val.unanswered.targetType === 'SingleTarget' && !val.unanswered.target) {
        ctx.addIssue({ code: 'custom', path: ['unanswered', 'target'], message: 'Required for a single target' });
      }
    }
  })
  .optional();

/** build_users/build_caps' pickup_group jsonb - Set-CsUserCallingSettings' CallGroup settings group. */
const pickupGroupSchema = z
  .object({
    order: z.enum(CALL_GROUP_ORDERS),
    targets: z.array(str(200)).max(25),
  })
  .strict()
  .optional();

/** One entry of build_users/build_caps' delegates jsonb array - New-CsUserCallingDelegate's own parameters. */
const delegateSchema = z.object({
  delegateUpn: str(200),
  makeCalls: z.boolean(),
  receiveCalls: z.boolean(),
  manageSettings: z.boolean(),
  pickUpHeldCalls: z.boolean(),
  joinActiveCalls: z.boolean(),
});
const delegatesSchema = z.array(delegateSchema).max(25).optional();

/**
 * Fields writable on a build_users/build_caps row beyond the natural key
 * (site_id/upn). Shared between create and patch - the Add dialog and the Edit
 * dialog show the same field set (see RecordDialog), so both must accept it.
 */
const buildIdentityWritable = {
  did: optStr(40),
  ext: optStr(20),
  number_type: numberType,
  revoke_ev: z.boolean().optional(),
  hold_uri: optStr(120),
  action: optStr(80),
  migration_wave: optStr(80),
  policies: policyMap,
  policy_ids: policyIdMap,
  // The Set-CsOnlineVoicemailUserSettings target - language is validated as
  // a Teams culture code here, so it's never stored in a form deployment
  // would then be rejected on.
  voicemail: z.object({ enabled: z.boolean().nullable().optional(), language: voicemailLanguage }).optional(),
  call_forwarding: callForwardingSchema,
  delegates: delegatesSchema,
  pickup_group: pickupGroupSchema,
  comments: optStr(2000),
  hidden: z.boolean().optional(),
  /** assign / move / clear this row's number in the same call - null clears it */
  phone_number_id: refId,
};

export const buildIdentityCreateSchema = z
  .object({ site_id: z.guid(), upn: upnSchema, ...buildIdentityWritable })
  .strict();
export type BuildIdentityCreateInput = z.infer<typeof buildIdentityCreateSchema>;

export const buildIdentityPatchSchema = z.object({ upn: upnSchema.optional(), ...buildIdentityWritable }).strict();
export type BuildIdentityPatchInput = z.infer<typeof buildIdentityPatchSchema>;

/**
 * Bulk-edit for build_users/build_caps: the same patch applied to many rows
 * at once. `upn`, `did` and `phone_number_id` are deliberately excluded -
 * they're per-row-unique identifiers, so applying the same value to
 * hundreds of rows would be destructive (duplicate UPNs, the same DID text,
 * or the same specific phone number claimed by every selected row) rather
 * than a real bulk operation.
 */
const { did: _bulkDid, phone_number_id: _bulkPhoneNumberId, ...buildBulkIdentityWritable } = buildIdentityWritable;
export const buildBulkPatchSchema = bulkPatchSchema(buildBulkIdentityWritable);
export type BuildBulkPatchInput = z.infer<typeof buildBulkPatchSchema>;

const buildCapWritable = {
  function: optStr(80),
  display_name: optStr(160),
  phone_model: optStr(120),
  device_config_profile: optStr(160),
  mac_address: optStr(40),
  serial_number: optStr(80),
  phone_location: optStr(160),
  lan_jack: optStr(80),
};

export const buildCapCreateSchema = buildIdentityCreateSchema.extend(buildCapWritable);
export type BuildCapCreateInput = z.infer<typeof buildCapCreateSchema>;

export const buildCapPatchSchema = buildIdentityPatchSchema.extend(buildCapWritable);
export type BuildCapPatchInput = z.infer<typeof buildCapPatchSchema>;

/** Quick add: just the UPNs; each is imported from the discovered tenant data. One per line or separated by commas, up to 200. */
export const buildQuickAddSchema = z
  .object({ site_id: z.guid(), upns: z.array(z.string().trim().min(1).max(320)).min(1).max(200) })
  .strict();
export type BuildQuickAddInput = z.infer<typeof buildQuickAddSchema>;

/** Bulk-seed build_users/build_caps from that site's discovery_users/discovery_caps. Idempotent. */
export const buildPopulateSchema = z.object({ site_id: z.guid() }).strict();
export type BuildPopulateInput = z.infer<typeof buildPopulateSchema>;

/**
 * Design & Build's "Link discovered resource account" action - pulls one
 * specific `tenant_objects` row (a live resource account not yet in Data
 * Collection at all) straight into this site, without the engineer having
 * to visit Site Data first.
 */
export const buildImportAndLinkResourceAccountSchema = z.object({ site_id: z.guid(), object_id: z.guid() }).strict();
export type BuildImportAndLinkResourceAccountInput = z.infer<typeof buildImportAndLinkResourceAccountSchema>;

/**
 * `live: false` on the automatic re-check after a targeted live check
 * completes - recomputes validation from the now-fresher data without
 * triggering another live check (see BuildService.validateSite).
 */
/** The engineer's answer to a number change on a user or common area phone. */
export const buildNumberDecisionSchema = z.object({ decision: z.enum(['accepted', 'rejected', 'reset']) }).strict();
export type BuildNumberDecisionInput = z.infer<typeof buildNumberDecisionSchema>;

export const buildValidateSchema = buildPopulateSchema.extend({ live: z.boolean().optional() }).strict();
export type BuildValidateInput = z.infer<typeof buildValidateSchema>;

/** rowIds omitted = every not-yet-created resource account on the site. */
export const buildResourceAccountRequestSchema = buildPopulateSchema.extend({
  rowIds: z.array(z.guid()).optional(),
}).strict();
export type BuildResourceAccountRequestInput = z.infer<typeof buildResourceAccountRequestSchema>;

const buildResourceAccountWritable = {
  location_id: optStr(80),
  number_type: numberType,
  voice_routing_policy: optStr(160),
  /** picker submits a tenant_policies.id - see BuildService.resolvePolicyIds */
  voice_routing_policy_id: refId,
  /** assign / move / clear this account's number in the same call - null clears it */
  phone_number_id: refId,
  /**
   * Confirms New-CsOnlineApplicationInstance has been run and the account
   * licensed (a manual step - see docs/DEPLOYMENT.md) so the number/policy
   * phase can proceed. The engineer flips this after running the generated
   * script; there's no live way to detect it automatically.
   */
  created: z.boolean().optional(),
};

export const buildResourceAccountCreateSchema = z
  .object({
    site_id: z.guid(),
    display_name: str(160).min(1),
    kind: z.enum(RESOURCE_ACCOUNT_KINDS),
    upn: upnSchema.optional(),
    ...buildResourceAccountWritable,
  })
  .strict();
export type BuildResourceAccountCreateInput = z.infer<typeof buildResourceAccountCreateSchema>;

export const buildResourceAccountPatchSchema = z
  .object({
    upn: upnSchema.optional(),
    display_name: optStr(160),
    kind: blankToNull(z.enum(RESOURCE_ACCOUNT_KINDS)),
    ...buildResourceAccountWritable,
  })
  .strict();
export type BuildResourceAccountPatchInput = z.infer<typeof buildResourceAccountPatchSchema>;

/** New-/Set-CsTeamsSharedCallingRoutingPolicy's own writable fields - see 0031_shared_calling_policies.sql. */
const buildSharedCallingPolicyWritable = {
  resource_account_id: refId,
  emergency_numbers: z.array(str(64)).max(20).optional(),
  description: optStr(500),
};

export const buildSharedCallingPolicyCreateSchema = z
  .object({
    site_id: z.guid(),
    name: str(160).min(1),
    ...buildSharedCallingPolicyWritable,
  })
  .strict();
export type BuildSharedCallingPolicyCreateInput = z.infer<typeof buildSharedCallingPolicyCreateSchema>;

export const buildSharedCallingPolicyPatchSchema = z
  .object({
    name: optStr(160),
    ...buildSharedCallingPolicyWritable,
  })
  .strict();
export type BuildSharedCallingPolicyPatchInput = z.infer<typeof buildSharedCallingPolicyPatchSchema>;

/**
 * build_call_queues' overflow/timeout jsonb - Set-CsCallQueue's
 * Overflow/Timeout parameter groups. `target` is a Guid, 'tel:' number, or
 * group id depending on `action` (see Set-CsCallQueue's own docs) - not
 * validated further here, same "trust the cmdlet to reject a bad value"
 * approach as call_forwarding's target fields.
 */
const callQueueActionSchema = (actions: readonly [string, ...string[]]) =>
  z
    .object({
      action: z.enum(actions).optional(),
      threshold: z.number().int().min(0).max(2700).optional(),
      target: optStr(200),
      /** PowerShell-only extras - see CallQueueActionSettings */
      priority: z.number().int().min(1).max(5).optional(),
      redirectPrompt: z.string().trim().max(1000).optional(),
      redirectAudio: z.object({ fileId: z.guid(), filename: z.string().trim().min(1).max(200) }).strict().optional(),
    })
    .strict()
    .optional();

const buildCallQueueWritable = {
  routing_method: blankToNull(z.enum(CALL_QUEUE_ROUTING_METHODS)),
  // RecordDialog's `type: 'number'` sends '' (then null, via buildPayload) for
  // an untouched/cleared field - treat as "use the DB default", not a
  // validation error (same pattern as discoveryNetworkSchema's mask/vlan_id).
  agent_alert_time: z.preprocess(
    (v) => (v === '' || v == null ? undefined : v),
    z.coerce.number().int().min(15).max(180).optional(),
  ),
  presence_based_routing: z.boolean().optional(),
  /** Agent UPNs - resolved to Entra object GUIDs at deploy time (Set-CsCallQueue -Users needs GUIDs). */
  agents: z.array(str(200)).max(200).optional(),
  overflow: callQueueActionSchema(CALL_QUEUE_OVERFLOW_ACTIONS),
  timeout: callQueueActionSchema(CALL_QUEUE_TIMEOUT_ACTIONS),
  /** Zero-agents-opted-in action - same shape as overflow/timeout, Set-CsCallQueue -NoAgentAction/-NoAgentActionTarget. */
  no_agent_action: callQueueActionSchema(CALL_QUEUE_NO_AGENT_ACTIONS),
  no_agent_apply_to: blankToNull(z.enum(CALL_QUEUE_NO_AGENT_APPLY_TO)),
  /** Required by Set-CsCallQueue when overflow/timeout/no-agent action is SharedVoicemail - checked in BuildService. */
  language_id: optStr(20),
  /** build_resource_accounts.id array - which RA(s)/phone numbers present this queue. */
  resource_accounts: z.array(z.guid()).max(10).optional(),
  notes: optStr(2000),
  /** PowerShell-only access settings (voice-app-access.ts): UPNs; null = not managed by Voxshift (Teams keeps its own). */
  authorized_users: z.array(str(200)).max(100).nullable().optional(),
  hide_authorized_users: z.array(str(200)).max(100).nullable().optional(),
  /** -SharedVoicemailTriageSettingsTemplateId (AI triage of shared voicemail; Microsoft private preview). */
  voicemail_triage_template_id: blankToNull(z.guid()),
};

export const buildCallQueueCreateSchema = z
  .object({ site_id: z.guid(), name: str(160).min(1), ...buildCallQueueWritable })
  .strict();
export type BuildCallQueueCreateInput = z.infer<typeof buildCallQueueCreateSchema>;

export const buildCallQueuePatchSchema = z.object({ name: optStr(160), ...buildCallQueueWritable }).strict();
export type BuildCallQueuePatchInput = z.infer<typeof buildCallQueuePatchSchema>;

/**
 * build_auto_attendants' narrative-only fields, carried through from
 * discovery_resource_accounts by Populate (see BuildService.populateResourceAccounts) -
 * a human-readable fallback alongside the structured fields below, never
 * read by planAutoAttendantRow.
 */
const buildAutoAttendantNarrative = {
  business_hours: optStr(400),
  ooh_action: optStr(400),
  holiday: optStr(400),
  advanced_features: optStr(400),
  notes: optStr(2000),
};

/**
 * Set-CsAutoAttendant's structured call-flow config - mirrors
 * BuildAutoAttendantsTable's columns (packages/db/src/schema.ts) and the
 * AutoAttendant* interfaces in deployment.ts exactly. Reverse-engineered
 * against OVP012's real live Auto Attendants and Microsoft Learn's
 * New-CsAutoAttendant construction chain this session - see the
 * "reverse-engineer OVP012" plan.
 */

/** A transfer target - New-CsAutoAttendantCallableEntity's -Type, plus the app-only 'auto_attendant'/'call_queue' kinds for same-site menu targets. */
const autoAttendantCallableEntitySchema = z
  .object({
    kind: z.enum(AA_CALLABLE_ENTITY_KINDS),
    /** build_auto_attendants.id or build_call_queues.id - same-site, app-enforced like policy_ids. */
    buildId: refId,
    /** Teams id of an auto attendant / call queue outside this site's design, kept as read from Teams. */
    liveId: refId,
    upn: optStr(200),
    number: optStr(64),
    /** M365 group Object ID - only for kind 'shared_voicemail' (New-CsAutoAttendantCallableEntity -Type SharedVoicemail -Identity). A raw GUID, no Find-CsGroup search yet. */
    groupId: refId,
  })
  .strict();

// New-CsOnlineTimeRange: weekly ranges must align to 15-minute boundaries
// (Microsoft Learn). An end of 24:00 means midnight at the end of the day
// (rendered as the TimeSpan 1.00:00).
const HHMM_RE = /^([01]\d|2[0-3]):(00|15|30|45)$/;
const HHMM_END_RE = /^(([01]\d|2[0-3]):(00|15|30|45)|24:00)$/;
const timeOfDay = () => z.string().regex(HHMM_RE, 'Use HH:mm, 24-hour, in 15-minute steps');

const autoAttendantTimeRangeSchema = z
  .object({ start: timeOfDay(), end: z.string().regex(HHMM_END_RE, 'Use HH:mm, 24-hour, in 15-minute steps (24:00 = end of day)') })
  .strict();

// Holiday bounds: ISO date or date-time (the canonical stored form, and the
// AA wizard's date picker), or Microsoft's own d/m/yyyy [H:mm] - normalized
// to New-CsOnlineDateTimeRange's input format at deploy time.
const DATE_BOUND_RE = /^(\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?)?(Z|[+-]\d{2}:?\d{2})?|\d{1,2}\/\d{1,2}\/\d{4}( \d{1,2}:\d{2})?)$/;
const dateBound = () => z.string().trim().regex(DATE_BOUND_RE, 'Use a date like 2026-12-25 or 2026-12-25T00:00:00');

const autoAttendantPromptSchema = z
  .object({
    type: z.enum(GREETING_TYPES),
    text: optStr(1000),
    /** files.id (an uploaded audio prompt) - only for type 'AudioFile'. */
    fileId: refId,
  })
  .strict();

const autoAttendantMenuOptionSchema = z
  .object({
    dtmf: z.enum(AA_DTMF_RESPONSES),
    action: z.enum(AA_MENU_OPTION_ACTIONS),
    target: autoAttendantCallableEntitySchema.optional(),
    /** New-CsAutoAttendantMenuOption -Prompt - only for action 'Announcement'. */
    prompt: autoAttendantPromptSchema.optional(),
  })
  .strict();

const autoAttendantMenuSchema = z
  .object({
    enableDialByName: z.boolean().optional(),
    directorySearchMethod: z.enum(AA_DIRECTORY_SEARCH_METHODS).optional(),
    options: z.array(autoAttendantMenuOptionSchema).max(12),
    /** New-CsAutoAttendantMenu -Prompts - the menu's own spoken prompt, distinct from the call flow's greetings. */
    prompts: z.array(autoAttendantPromptSchema).max(5).optional(),
  })
  .strict();

const autoAttendantCallFlowSchema = z
  .object({
    greetings: z.array(autoAttendantPromptSchema).max(5),
    menu: autoAttendantMenuSchema,
  })
  .strict();

const autoAttendantWeekdayHours = () => z.array(autoAttendantTimeRangeSchema).max(4);

const autoAttendantScheduleSchema = z
  .object({
    type: z.enum(AA_SCHEDULE_TYPES),
    weekly: z
      .object({
        monday: autoAttendantWeekdayHours(),
        tuesday: autoAttendantWeekdayHours(),
        wednesday: autoAttendantWeekdayHours(),
        thursday: autoAttendantWeekdayHours(),
        friday: autoAttendantWeekdayHours(),
        saturday: autoAttendantWeekdayHours(),
        sunday: autoAttendantWeekdayHours(),
        /** New-CsOnlineSchedule -Complement - the hours above are business hours; this schedule fires outside them. */
        complement: z.boolean().optional(),
      })
      .strict()
      .optional(),
    fixed: z
      .object({
        /** Holiday date ranges - canonical form is ISO date-times with an exclusive end (see normalizeFixedRange in deployment.ts). An empty end means a single day. */
        ranges: z.array(z.object({ start: dateBound(), end: z.union([dateBound(), z.literal('')]) }).strict()).max(10),
      })
      .strict()
      .optional(),
  })
  .strict();

const autoAttendantHolidayCallFlowSchema = z
  .object({
    name: str(160).min(1),
    callFlow: autoAttendantCallFlowSchema,
    schedule: autoAttendantScheduleSchema,
  })
  .strict();

const buildAutoAttendantWritable = {
  ...buildAutoAttendantNarrative,
  /** build_resource_accounts.id array - which RA(s)/phone numbers this AA answers on. Mirrors buildCallQueueWritable's own field - an AA can have several resource accounts, or none yet. */
  resource_accounts: z.array(z.guid()).max(10).optional(),
  /** Set-CsAutoAttendant -LanguageId. */
  language_id: optStr(20),
  /** Set-CsAutoAttendant -TimeZoneId. */
  time_zone_id: optStr(80),
  /** Set-CsAutoAttendant -VoiceId. */
  voice_id: optStr(20),
  /** Set-CsAutoAttendant -EnableVoiceResponse. */
  voice_response_enabled: z.boolean().optional(),
  operator: autoAttendantCallableEntitySchema.nullable().optional(),
  /** Business hours (or 24/7 if no after-hours flow is set) - Set-CsAutoAttendant -DefaultCallFlow. */
  default_call_flow: autoAttendantCallFlowSchema.nullable().optional(),
  after_hours_call_flow: autoAttendantCallFlowSchema.nullable().optional(),
  holiday_call_flows: z.array(autoAttendantHolidayCallFlowSchema).max(10).optional(),
  /** The after-hours schedule - each holiday_call_flows entry carries its own schedule instead. */
  schedule: autoAttendantScheduleSchema.nullable().optional(),
  /** PowerShell-only access settings (voice-app-access.ts): UPNs; null = not managed by Voxshift (Teams keeps its own). */
  authorized_users: z.array(str(200)).max(100).nullable().optional(),
  hide_authorized_users: z.array(str(200)).max(100).nullable().optional(),
  /** -UserNameExtension: what dial-by-name adds after a name two people share. null = not managed. */
  user_name_extension: z.enum(USER_NAME_EXTENSIONS).nullable().optional(),
};

export const buildAutoAttendantCreateSchema = z
  .object({ site_id: z.guid(), name: str(160).min(1), ...buildAutoAttendantWritable })
  .strict();
export type BuildAutoAttendantCreateInput = z.infer<typeof buildAutoAttendantCreateSchema>;

export const buildAutoAttendantPatchSchema = z.object({ name: optStr(160), ...buildAutoAttendantWritable }).strict();
export type BuildAutoAttendantPatchInput = z.infer<typeof buildAutoAttendantPatchSchema>;

/**
 * Per site: which real tenant_policies row a Site Data generic
 * calling-policy catalog entry ("International", "Standard", …) resolves to
 * for THIS site - see BuildService.assertCallingPoliciesMapped, which blocks
 * Populate until every catalog entry actually in use has one of these.
 */
export const callingPolicySiteMapSetSchema = z
  .object({
    site_id: z.guid(),
    discovery_calling_policy_id: z.guid(),
    tenant_policy_id: z.guid(),
  })
  .strict();
export type CallingPolicySiteMapSetInput = z.infer<typeof callingPolicySiteMapSetSchema>;

/**
 * A named, reusable preset of policy targets + voicemail defaults for Users
 * or CAPs on one site ("Standard User Template") - the one marked
 * `is_default` seeds every new row Populate creates for that site+kind;
 * any template can also be applied on demand to already-populated rows.
 */
export const buildTemplateCreateSchema = z
  .object({
    site_id: z.guid(),
    kind: z.enum(['user', 'cap']),
    name: str(160).min(1),
    policy_ids: policyIdMap,
    voicemail_enabled: z.boolean().nullable().optional(),
    voicemail_language: voicemailLanguage,
    is_default: z.boolean().optional(),
  })
  .strict();
export type BuildTemplateCreateInput = z.infer<typeof buildTemplateCreateSchema>;

export const buildTemplatePatchSchema = buildTemplateCreateSchema.partial().strict();
export type BuildTemplatePatchInput = z.infer<typeof buildTemplatePatchSchema>;

/** Apply one template's policy_ids/voicemail to a set of already-populated rows. */
export const buildTemplateApplySchema = z
  .object({
    ids: z.array(z.guid()).min(1).max(500),
  })
  .strict();
export type BuildTemplateApplyInput = z.infer<typeof buildTemplateApplySchema>;

export const buildTemplateListQuerySchema = z.object({ siteId: z.guid(), kind: z.enum(['user', 'cap']).optional() }).strict();
export type BuildTemplateListQuery = z.infer<typeof buildTemplateListQuerySchema>;

export const callingPolicyMapListQuerySchema = z.object({ siteId: z.guid() }).strict();
export type CallingPolicyMapListQuery = z.infer<typeof callingPolicyMapListQuerySchema>;

export const buildListQuerySchema = z.object({
  siteId: z.guid(),
  q: z.string().trim().max(160).optional(),
  // Not z.coerce.boolean(): that turns the query-string text "false" into true.
  hidden: z.preprocess((v) => (v === 'true' ? true : v === 'false' ? false : v), z.boolean()).optional(),
  /** Only rows designed for this service request (its Design tab). */
  serviceRequestId: z.guid().optional(),
  page: z.coerce.number().int().min(1).max(100000).default(1),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type BuildListQuery = z.infer<typeof buildListQuerySchema>;

/* ------------------------------ Files DTOs ------------------------------ */

export const filesQuerySchema = z.object({
  siteId: z.guid().optional(),
  category: z.enum(['deployment_change_document', 'number_port_document']).optional(),
  sourceType: z.string().max(60).optional(),
  sourceId: z.guid().optional(),
  q: z.string().trim().max(200).optional(),
});
export type FilesQuery = z.infer<typeof filesQuerySchema>;

/* ------------------------------ Shared search DTO ------------------------------ */

/** The plain `?q=` search param, for list endpoints with nothing else to filter by. */
export const searchQuerySchema = z.object({ q: z.string().trim().max(200).optional() }).strict();
export type SearchQuery = z.infer<typeof searchQuerySchema>;


/* ============================ Managed Services ============================ */

/** Super Admin switch: let this customer raise service requests. */
export const updateTenantManagedServicesSchema = z.object({ enabled: z.boolean() }).strict();
export type UpdateTenantManagedServicesInput = z.infer<typeof updateTenantManagedServicesSchema>;

/** Super Admin switch: show this customer the Monitoring section (Devices, Watch list, Alerts) and let the platform watch their devices. */
export const updateTenantMonitoringSchema = z.object({ enabled: z.boolean() }).strict();
export type UpdateTenantMonitoringInput = z.infer<typeof updateTenantMonitoringSchema>;

const UPN_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const srPersonSchema = z.object({
  upn: z.string().trim().regex(UPN_RE, 'Enter a sign-in address like name@company.com').max(200),
  name: z.string().trim().min(1).max(120),
}).strict();
const srTargetSchema = z
  .object({
    kind: z.enum(SR_TARGET_KINDS),
    id: z.guid().optional(),
    upn: z.string().trim().regex(UPN_RE).max(200).optional(),
    label: z.string().trim().min(1).max(200),
  })
  .strict()
  .superRefine((t, ctx) => {
    if ((t.kind === 'call_queue' || t.kind === 'auto_attendant') && !t.id) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Choose which one' });
    if (t.kind === 'person' && !t.upn) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Choose a person' });
  });

/** One field's value. Required-ness is applied by srDetailsSchema, which knows whether the field is shown. */
function srFieldSchema(f: SrFieldSpec): z.ZodTypeAny {
  switch (f.kind) {
    case 'text':
    case 'textarea':
      return z.string().trim().max(f.max ?? (f.kind === 'text' ? 200 : 4000));
    case 'number':
      return z.number({ error: `${f.label} must be a number` }).int().min(f.min ?? 0).max(f.max ?? 100000);
    case 'select':
      return z.enum((f.options ?? []) as [string, ...string[]], { error: () => `Choose ${f.label.toLowerCase()}` });
    case 'boolean':
      return z.boolean();
    case 'date':
      return z.string().regex(/^\d{4}-\d{2}-\d{2}$/, `${f.label} must be a date`);
    case 'person':
      return srPersonSchema;
    case 'people':
      return z.array(srPersonSchema).max(f.max ?? 100);
    case 'phone_number':
      return z.string().trim().regex(/^\+\d{6,15}$/, `${f.label} must be a phone number`);
    case 'phone_numbers':
      return z
        .array(z.string().trim().regex(/^\+\d{6,15}$/, `${f.label} must be phone numbers`))
        .max(f.max ?? 200)
        .refine((v) => new Set(v).size === v.length, 'Pick each number once');
    case 'target':
      return srTargetSchema;
    case 'menu':
      return z
        .array(z.object({ key: z.enum(SR_MENU_KEYS), target: srTargetSchema }).strict())
        .max(f.max ?? 10)
        .refine((opts) => new Set(opts.map((o) => o.key)).size === opts.length, 'Each key can only be used once');
    case 'number_range':
      return z.object({ id: z.guid(), label: z.string().trim().min(1).max(200) }).strict();
    case 'device_model':
      return z.string().trim().min(1).max(120);
    case 'country':
      return z.enum(COUNTRY_CODES, { error: () => 'Choose a country' });
    case 'choices':
      return z
        .array(z.enum((f.options ?? []) as [string, ...string[]]))
        .max((f.options ?? []).length)
        .refine((v) => new Set(v).size === v.length, 'Tick each option once');
    case 'site_object':
      return z.object({ id: z.guid(), label: z.string().trim().min(1).max(200) }).strict();
  }
}

function srIsBlank(v: unknown): boolean {
  return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
}

/**
 * The details object for one request type, built from its field specs. Unknown
 * keys are rejected. A field hidden by showWhen is dropped; a shown required
 * field must be answered; blank optional answers are dropped.
 */
export function srDetailsSchema(type: SrType) {
  const fields = SR_TYPE_DEFS[type].fields;
  // SR_NEW_NUMBERS_KEY is set by the API, never by the form - it stays an unknown key here.
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const f of fields) shape[f.key] = z.unknown().optional();
  return z
    .object(shape)
    .strict()
    .transform((raw, ctx) => {
      const out: Record<string, unknown> = {};
      for (const f of fields) {
        if (!srFieldVisible(f, raw, fields)) continue;
        let v = raw[f.key];
        if (typeof v === 'string') v = v.trim();
        if (srIsBlank(v)) {
          if (f.required) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [f.key], message: `${f.label} is required` });
          continue;
        }
        const parsed = srFieldSchema(f).safeParse(v);
        if (!parsed.success) {
          for (const issue of parsed.error.issues) ctx.addIssue({ ...issue, path: [f.key, ...issue.path] });
          continue;
        }
        out[f.key] = parsed.data;
      }
      return out;
    });
}

export const createServiceRequestSchema = z
  .object({
    type: z.enum(SR_TYPES),
    title: z.string().trim().min(3, 'Give the request a short title').max(160),
    priority: z.enum(SR_PRIORITIES).default('normal'),
    siteId: z.guid().nullable().optional(),
    targetDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullable()
      .optional(),
    details: z.record(z.string(), z.unknown()).default({}),
  })
  .superRefine((v, ctx) => {
    const parsed = srDetailsSchema(v.type).safeParse(v.details);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) ctx.addIssue({ ...issue, path: ['details', ...issue.path] });
    }
    if (SR_TYPE_DEFS[v.type].needsSite && !v.siteId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['siteId'], message: 'Choose the site this request is for' });
    }
  })
  .transform((v) => ({ ...v, details: srDetailsSchema(v.type).parse(v.details) as Record<string, unknown> }));
export type CreateServiceRequestInput = z.infer<typeof createServiceRequestSchema>;

/** POST /service-requests/check - the form's live warnings. Details are checked loosely: it runs while the form is half filled in. */
export const createServiceRequestCheckSchema = z
  .object({
    type: z.enum(SR_TYPES),
    siteId: z.guid().nullable().optional(),
    details: z.record(z.string(), z.unknown()).default({}),
  })
  .strict();
export type CreateServiceRequestCheckInput = z.infer<typeof createServiceRequestCheckSchema>;

export const serviceRequestStatusSchema = z.object({
  to: z.enum(SR_STATUSES),
  note: z.string().trim().max(4000).optional(),
});
export type ServiceRequestStatusInput = z.infer<typeof serviceRequestStatusSchema>;

export const serviceRequestAssignSchema = z
  .object({ userId: z.guid().nullable().optional(), agentId: z.guid().optional() })
  .refine((v) => v.userId !== undefined || !!v.agentId, { message: 'Pick a person, an assistant, or nobody.' });
export type ServiceRequestAssignInput = z.infer<typeof serviceRequestAssignSchema>;

/** POST /service-requests/:id/incident - link a request to an incident, or remove the link. */
export const serviceRequestIncidentLinkSchema = z.object({ incidentId: z.guid().nullable() }).strict();

export const serviceRequestCommentSchema = z.object({
  body: z.string().trim().min(1, 'Write a comment').max(4000),
  /** Staff-only note the customer never sees. */
  internal: z.boolean().optional(),
  /** Team only, public: a question - the request waits on the customer until they reply. */
  waitForReply: z.boolean().optional(),
});
export type ServiceRequestCommentInput = z.infer<typeof serviceRequestCommentSchema>;

export const listServiceRequestsQuerySchema = z.object({
  status: z.enum([...SR_STATUSES, 'open', 'all'] as [string, ...string[]]).default('open'),
  type: z.enum(SR_TYPES).optional(),
});
export type ListServiceRequestsQuery = z.infer<typeof listServiceRequestsQuerySchema>;

/** Engineers and admins: whether a site takes service requests (operations) or not yet (project). */
export const setSiteModeSchema = z.object({ mode: z.enum(SITE_MODES) }).strict();
export type SetSiteModeInput = z.infer<typeof setSiteModeSchema>;

/** Super Admin: which MSP looks after a customer (null = none). */
export const setTenantMspSchema = z.object({ mspId: z.guid().nullable() }).strict();
export type SetTenantMspInput = z.infer<typeof setTenantMspSchema>;

export const mspServiceRequestsQuerySchema = z.object({
  status: z.enum([...SR_STATUSES, 'open', 'all'] as [string, ...string[]]).default('open'),
  /** Super Admins only: limit to one MSP ('none' = customers with no MSP). */
  mspId: z.union([z.guid(), z.literal('none')]).optional(),
  /** Only requests assigned to me. */
  mine: z.enum(['true', 'false']).optional(),
});
export type MspServiceRequestsQuery = z.infer<typeof mspServiceRequestsQuerySchema>;

/** Carrier side of a port-in range (`PATCH .../number-ranges/:id/port`): send only what changed. An empty date clears it. */
export const portCarrierDetailsSchema = z
  .object({
    newCarrier: optStr(160),
    portOrderRef: optStr(120),
    focDate: z.union([z.literal(''), z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'use a date')]).nullable().optional(),
    /** true records that the port was submitted to the new carrier (now); false takes it back */
    submittedToCarrier: z.boolean().optional(),
  })
  .strict();
export type PortCarrierDetailsInput = z.infer<typeof portCarrierDetailsSchema>;

/** The port finished at the carrier (`POST .../number-ranges/:id/port/complete`): the carrier switches to the new one. */
export const portCompleteSchema = z
  .object({ completedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'use a date').optional() })
  .strict();
export type PortCompleteInput = z.infer<typeof portCompleteSchema>;

/**
 * Email the picked validation issues to a site's contacts (`POST build/issues/email`). Each issue is named by what the Validation
 * issues tab showed (sheet, row, code and its wording); the server checks every one against the site's current issues and only
 * sends what is still an issue, in its own words. `recipients` narrows to some of the site contacts.
 */
export const emailBuildIssuesSchema = z
  .object({
    site_id: z.guid(),
    issues: z
      .array(z.object({ sheet: z.enum(['users', 'caps', 'resource-accounts']), row_id: z.guid(), code: str(60), detail: str(2000) }).strict())
      .min(1)
      .max(200),
    message: optStr(2000),
    recipients: z.array(z.email()).min(1).max(50).optional(),
  })
  .strict();
export type EmailBuildIssuesInput = z.infer<typeof emailBuildIssuesSchema>;

/** A person a site's issues email would go to. */
export interface BuildIssueRecipient {
  email: string;
  name: string | null;
  /** a user of the system, or a contact typed on the site's Overview */
  kind: 'user' | 'typed';
}
export interface EmailBuildIssuesResult {
  sent: number;
  issues: number;
  /** picked issues that are no longer issues, so were left out */
  skipped: number;
}

/* ------------------------- Handset tracker (site Overview) ------------------------- */

/** PUT sites/:siteId/handsets - the stage one handset model has reached; "not_ordered" clears it. */
export const siteHandsetStatusSchema = z
  .object({ model: z.string().trim().min(1).max(120), status: z.enum(['not_ordered', 'ordered', 'delivered', 'in_tooling', 'installed']) })
  .strict();
export type SiteHandsetStatusInput = z.infer<typeof siteHandsetStatusSchema>;
